// src/components/StatusCard.tsx
import React from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

export type StatusVariant = 'not-found' | 'empty' | 'error';
export type StatusLayout = 'card' | 'page';

export interface StatusCardProps {
  variant: StatusVariant;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  Icon?: SvgIconComponent;
  imageSrc?: string;
  layout?: StatusLayout;
}

const defaultIcons: Record<StatusVariant, SvgIconComponent> = {
  'not-found': require('@mui/icons-material/SearchOff').default,
  empty: require('@mui/icons-material/Inbox').default,
  error: require('@mui/icons-material/ErrorOutline').default,
};

const StatusCard: React.FC<StatusCardProps> = ({
  variant,
  title,
  message,
  actionLabel,
  onAction,
  Icon,
  imageSrc,
  layout = 'card',
}) => {
  const ChosenIcon = Icon ?? defaultIcons[variant];
  const iconSize = layout === 'page' ? 96 : 64;
  const titleVariant = layout === 'page' ? 'h3' : 'h4';
  const messageVariant = layout === 'page' ? 'body1' : 'body2';
  const buttonVariant = layout === 'page' ? 'contained' : 'outlined';

  const commonContent = (
    <>
      {imageSrc ? (
        <Box
          component="img"
          src={imageSrc}
          alt={title}
          sx={{ maxWidth: layout === 'page' ? 250 : 200, mb: 4, mx: 'auto' }}
        />
      ) : (
        <Box sx={{ fontSize: iconSize, color: 'text.disabled', mb: 2 }}>
          <ChosenIcon fontSize="inherit" />
        </Box>
      )}

      <Typography variant={titleVariant} gutterBottom>
        {title}
      </Typography>

      <Typography variant={messageVariant} color="text.secondary" sx={{ mb: 3 }}>
        {message}
      </Typography>

      {actionLabel && onAction && (
        <Button variant={buttonVariant} onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </>
  );

  if (layout === 'page') {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: 800,
          mx: 'auto',
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 3,
        }}
      >
        {commonContent}
      </Box>
    );
  }

  return (
    <Card elevation={0} sx={{ my: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
      <CardContent>{commonContent}</CardContent>
    </Card>
  );
};

export default StatusCard;